import { memo, useCallback, useMemo, useState } from "react";

import { parseGid, useCart } from "@shopify/hydrogen-react";

import { useCartActiveProduct } from "@cart/CartActiveProductContext";
import { CartUpsellItemView } from "@cart/CartUpsellItem/CartUpsellItem.view";
import { useCartAnalytics } from "@react/hooks/useCartAnalytics";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import { isDefaultOption, handleize } from "@react/utilities";
import { STOREFRONT_CART_ATTRIBUTES } from "@tablescape/constants";
import { TablescapeEvent, TablescapeContext } from "@tablescape/context";
import {
  type CartSectionSettings,
  type CartUpsellProductMetadata,
} from "@tablescape/state";
import { getMsrpForVariant } from "@tablescape/utilities";

const CartUpsellItemBase = ({
  position,
  upsell,
}: {
  position: number;
  upsell: CartUpsellProductMetadata;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toggleCartDrawer } = useCartActiveProduct();
  const { id, lines } = useCart();
  const {
    meta: { customer },
  } = useSectionSettings<CartSectionSettings>();
  const { reportUpsellAddToCartEvent } = useCartAnalytics();

  const handleUpsellAddToCart = useCallback(() => {
    if (!id) {
      return;
    }

    setIsLoading(true);

    const variantId = parseGid(upsell.selectedVariant.id).id;
    TablescapeContext.emit(TablescapeEvent.ADD_TO_CART, {
      cart: {
        lines: [
          {
            attributes: [
              {
                key: STOREFRONT_CART_ATTRIBUTES.MSRP,
                value: getMsrpForVariant(upsell.selectedVariant),
              },
              {
                key: STOREFRONT_CART_ATTRIBUTES.UPSELL,
                value: String(true),
              },
              ...(upsell.relatedCartLines
                ? [
                    {
                      key: STOREFRONT_CART_ATTRIBUTES.RELATED_ITEMS,
                      value: [
                        variantId,
                        ...upsell.relatedCartLines.map(
                          (cartLine) => parseGid(cartLine?.merchandise?.id).id
                        ),
                      ].join(";"),
                    },
                  ]
                : []),
            ],
            id: variantId,
            quantity: 1,
          },
        ],
        bundleProduct: undefined,
      },
    });

    reportUpsellAddToCartEvent({ customer, id, lines, position, upsell });
  }, [customer, id, lines, position, reportUpsellAddToCartEvent, upsell]);

  const handleOpenProductVariantDrawer = useCallback(
    () =>
      toggleCartDrawer(
        {
          productVariantDrawerOpen: true,
        },
        {
          activeOptions: upsell.selectedOptions,
          activeProduct: upsell.product,
          activeUpsell: { upsell, position },
          activeVariant: upsell.selectedVariant,
          isGiftWithPurchase: false,
          isProductUpdate: false,
        }
      ),
    [position, toggleCartDrawer, upsell]
  );

  const selectedOption = useMemo(() => {
    const currentOption = upsell.selectedOptions.find(
      (o) => !isDefaultOption(o)
    );

    if (currentOption) {
      return { ...currentOption, handle: handleize(currentOption.value) };
    }

    return undefined;
  }, [upsell.selectedOptions]);

  return (
    <CartUpsellItemView
      handleOpenProductVariantDrawer={handleOpenProductVariantDrawer}
      handleUpsellAddToCart={handleUpsellAddToCart}
      isLoading={isLoading}
      selectedOption={selectedOption}
      selectedVariant={upsell.selectedVariant}
      upsellProduct={upsell.product}
    />
  );
};

export const CartUpsellItem = memo(CartUpsellItemBase);
